import axios from 'axios';
import ApiResponseError from '../../infrastructure/Exceptions/ApiResponseError';
import { ErrorTypes } from '../../infrastructure/Exceptions/utils/ResponseErrorUtils';
import { encodeUrlQueryParameters } from '../../infrastructure/serializer/axios_serializers';
import { EVENT_CATEGORIES, generalAppInsightsTrack } from '../../utilities/applicationInsights';
import { isArrayEmpty } from '../../utilities/arraysUtils';
import AuthClient from '../auth/AuthClient';
import { findMissingRequiredFields } from './findMissingRequiredFields';

const RequestMethods = {
  GET: 'GET',
  PUT: 'PUT',
  POST: 'POST',
  DELETE: 'DELETE',
  PATCH: 'PATCH'
};
const headerJson = { 'Content-Type': 'application/json' };

const buildRequest = (overrides, requiredFields) => ({
  headers: headerJson,
  ...overrides,
  requiredFields
});

const authClient = new AuthClient();
const renewToken = async (addApiLog = false) => {
  var user = authClient.getUser();
  if (user) {
    // check to see if we should renew the access token
    if (user.accessTokenExpiry < new Date().getTime()) {
      await authClient.renewToken(addApiLog);
    }
  }
};

/**
 * Abstract wrapper class to call the API Endpoints
 * @abstract
 * @class GWRApiClient
 */
export default class GWRApiClient {
  /**
   * @constructor
   * If a token is provided, then we use that token and we set isLoggedIn as true to perform the API calls
   * If there isn't any token provided, then we use the one from the current logged user.
   * @param {string} url
   *  The url where the service endpoints are
   * @param {string} token
   *  The token of the logged user
   */
  constructor(url = '', token = '') {
    this.url = url;
    this.token = token;
    this.isLoggedIn = token !== '';
    this.isReservationOnlyToken = false;

    if (token === '') {
      var user = authClient.getUser();
      if (user && user.id && !user.isReservationOnlyToken) {
        this.token = user.accessToken;
        this.isLoggedIn = true;
      } else {
        this.isReservationOnlyToken = user?.isReservationOnlyToken ?? false;
      }
    }

    encodeUrlQueryParameters();
  }

  /**
   * @function
   * Upper wrapper method to perform the call to the API.
   * @param {string} urlRequest
   * The url to the API endpoint
   * @param {RequestMethods} method
   * The http verb
   * @param {object} requestObject
   * All the information regarding the request: params (query string), data and headers
   * @param {boolean} [isGwlEndpoint=true]
   * Defaults to true for GWL endpoints. Set to false for third-party endpoints (skips credentials and auth headers).
   */
  async apiCall(urlRequest, method, requestObject, isGwlEndpoint = true) {
    const clientTypeHeader = isGwlEndpoint ? { 'x-client-type': 'web' } : {};
    const requestObjectWithCredentials = {
      ...requestObject,
      withCredentials: isGwlEndpoint,
      headers: { ...requestObject.headers, ...clientTypeHeader }
    };

    // GET REQUIRED FIELDS
    const requiredFields = requestObject.requiredFields;

    // VALIDATE REQURED FIELDS ON PARAMS OR DATA
    if (requiredFields && !isArrayEmpty(requiredFields)) {
      const hasParams = 'params' in requestObject;
      const hasData = 'data' in requestObject;

      const throwIfMissingRequiredFields = values => {
        const missingFields = findMissingRequiredFields(requiredFields, values);
        if (isArrayEmpty(missingFields)) return;
        this.throwResponseError(ErrorTypes.MissingRequiredField, null, missingFields.join(', '));
      };

      if (hasParams) {
        throwIfMissingRequiredFields(requestObject.params);
      }
      if (hasData) {
        throwIfMissingRequiredFields(requestObject.data);
      }
    }

    try {
      const requestConfig = {
        withCredentials: isGwlEndpoint,
        ...(isGwlEndpoint
          ? {
              headers: { ...requestObject.headers, ...clientTypeHeader }
            }
          : {})
      };

      switch (method) {
        case RequestMethods.GET:
          return await axios.get(urlRequest, requestObjectWithCredentials);
        case RequestMethods.PUT:
          return await axios.put(urlRequest, requestObject.data, requestConfig);
        case RequestMethods.POST:
          return await axios.post(urlRequest, requestObject.data, requestConfig);
        case RequestMethods.DELETE:
          return await axios.delete(urlRequest, requestObjectWithCredentials);
        case RequestMethods.PATCH:
          return await axios.patch(urlRequest, requestObject.data, requestConfig);
        default:
          return undefined;
      }
    } catch (err) {
      this.throwResponseError(ErrorTypes.ResponseError, err);
    }
  }

  /**
   * @function
   * Function to call a GET request.
   * @param {string} urlRequest
   * @param {object} params
   * sent as a query string parameters
   * @param {string[]} [requiredFields=[]]
   * @param {boolean} [isGwlEndpoint=true]
   * Defaults to true for GWL endpoints. Set to false for third-party endpoints (skips renewToken and auth headers).
   * @param addApiLog THIS IS TEMPORARY TO TEST THE PAYMENT LOGS
   * @param {AbortSignal} [signal]
   * Optional AbortController signal to allow the caller to cancel the underlying request.
   */
  async getApiCallAuth(urlRequest, params = {}, requiredFields = [], isGwlEndpoint = true, addApiLog = false, signal) {
    if (isGwlEndpoint) await renewToken(addApiLog);
    if (addApiLog) {
      generalAppInsightsTrack(`API call after renew ${urlRequest}`, EVENT_CATEGORIES.apiGeneralError);
    }
    return this.apiCall(
      urlRequest,
      RequestMethods.GET,
      buildRequest({ params, signal }, requiredFields),
      isGwlEndpoint
    );
  }

  /**
   * @function
   * Function to call a PUT request.
   * @param {string} urlRequest
   * @param {object} data
   * Payload for the request
   * @param {string[]} [requiredFields=[]]
   */
  async putApiCallAuth(urlRequest, data, requiredFields = []) {
    await renewToken();
    return this.apiCall(urlRequest, RequestMethods.PUT, buildRequest({ data }, requiredFields));
  }

  /**
   * @function
   * Function to call a POST request.
   * @param {string} urlRequest
   * @param {object} data
   * Payload for the request
   * @param {string[]} [requiredFields=[]]
   * @param {boolean} [isGwlEndpoint=true]
   * Defaults to true for GWL endpoints. Set to false for third-party endpoints (skips renewToken and auth headers).
   */
  async postApiCallAuth(urlRequest, data, requiredFields = [], isGwlEndpoint = true) {
    if (isGwlEndpoint) await renewToken();
    return this.apiCall(urlRequest, RequestMethods.POST, buildRequest({ data }, requiredFields), isGwlEndpoint);
  }

  /**
   * @function
   * Function to call a DELETE request.
   * @param {string} urlRequest
   * @param {object} data
   * Payload for the request
   * @param {string[]} [requiredFields=[]]
   */
  async deleteApiCallAuth(urlRequest, data, requiredFields = []) {
    await renewToken();
    return this.apiCall(urlRequest, RequestMethods.DELETE, buildRequest({ data }, requiredFields));
  }

  /**
   * @function
   * Function to call a PATCH request.
   * @param {string} urlRequest
   * @param {object} data
   * Payload for the request
   * @param {string[]} [requiredFields=[]]
   */
  async patchApiCallAuth(urlRequest, data, requiredFields = []) {
    await renewToken();
    return this.apiCall(urlRequest, RequestMethods.PATCH, buildRequest({ data }, requiredFields));
  }

  /**
   * @function
   * Error manager
   * @param {ErrorTypes} errorType
   * @param {object} err
   * @param {string} details
   * Any further details for the provided error
   */
  throwResponseError(errorType, err = {}, details = '') {
    throw new ApiResponseError(errorType, err, details);
  }
}
